import { useRef } from 'react';

import { ia, iaRa, io } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';

import Popover from '../Popovers/Popover/Popover';

import AppliedFilters from './components/AppliedFilters';
import { getFormulaOptions, signatureStatusOptions } from './lib/helpers';

const Filters = ({
  defaultFilters,
  filters,
  changeFilters,
  updateFilters,
  appliedFilters = true,
  position,
  ...rest
}) => {
  const popoverRef = useRef(null);

  const handleRemoveFilter = (key) => {
    changeFilters((prev) => {
      if (!ia(prev[key])) return prev;
      const filteredArray = prev[key].slice(0, -1);
      if (!ia(filteredArray)) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return {
        ...prev,
        [key]: filteredArray
      };
    });
  };

  const onChangeHandler = (items, key, formula, checked) => {
    if (!ia(items) && !io(items)) {
      handleRemoveFilter(key);
      return;
    }

    changeFilters((prev) => {
      const existing = iaRa(prev?.[key]);
      const isApplied = ia(items, 1);
      const activeFormula = formula || defaultFilters[key]?.formula;

      const newFilter = {
        ...defaultFilters[key],
        values: items,
        formula: activeFormula || 'is',
        ...(ia(items) &&
          activeFormula !== 'is not' &&
          !signatureStatusOptions.includes(activeFormula) && {
            formula: items.length > 1 ? 'is any of' : 'is'
          })
      };

      if (!isApplied && !checked) {
        return {
          ...prev,
          [key]: [...existing, newFilter]
        };
      }

      const updated = [...existing];
      updated[updated.length - 1] = newFilter;

      return {
        ...prev,
        [key]: updated
      };
    });
  };

  const handleOptionClick = (option, key) => {
    changeFilters((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        {
          ...defaultFilters[key],
          values: option.type === 'range' ? { from: null, to: null } : '',
          formula: option.value,
          type: option.type
        }
      ]
    }));
  };

  const filterOptions = Object.entries(defaultFilters || {})
    .filter(([_, filter]) => !filter.default && filter.type !== 'search')
    .map(([key, filter]) => ({
      label: filter.title,
      value: key,
      icon: filter.icon,
      children: getFormulaOptions(
        filter?.type,
        iaRa(filter?.values).length,
        filter?.values,
        filter?.allowed_operators
      ).map((option) => ({
        ...option,
        ...(option?.clickable && {
          onClick: () => handleOptionClick(option, key)
        }),
        ...(option?.children && {
          children: iaRa(option.children).map((child) => {
            const Component = child.component;
            return {
              ...child,
              component: ({ handleClose }) => (
                <Component
                  dataKey={key}
                  data={filter}
                  value={filter?.values}
                  formula={option?.value}
                  onChange={onChangeHandler}
                  dropdownClassName="-top-[16px] left-0 z-50"
                  onClose={handleClose}
                />
              )
            };
          })
        })
      })),
      ...filter,
      key
    }));

  return (
    <div className="flex flex-1 flex-wrap items-start gap-2">
      {appliedFilters && (
        <AppliedFilters
          filters={filters}
          changeFilters={changeFilters}
          updateFilters={updateFilters}
          defaultFilters={defaultFilters}
          position={position}
        />
      )}

      {ia(filterOptions) && (
        <div className="relative flex-1" ref={popoverRef}>
          <Popover
            customButtonTrigger={({ open }) => (
              <Button
                variant="tertiary"
                text="Filter"
                textClassName="select-none"
                icon="sort-linear"
                size="xs"
                color="neutral"
                className={open && 'bg-neutral-100'}
              />
            )}
            optionArrowIcon="chevron-right-filled"
            optionIconColor="neutral"
            iconsShade={700}
            options={filterOptions}
            textClassName="text-neutral-900"
            isFixed
            position={position}
            {...rest}
          />
        </div>
      )}
    </div>
  );
};

export default Filters;
